import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useNavigate } from "react-router-dom";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";
import HawkFindCard from "../components/HawkFindCard";
import CreateHawkFindModal from "../components/CreateHawkFindModal";
import ConfirmActionModal from "../components/ConfirmActionModal";
import { useUserContext } from "../context/UserContext";
import {
  HAWK_FIND_REACTIONS,
  deleteHawkFind,
  getHawkFinds,
  getHawkFindsQueryKey,
  setHawkFindReaction,
  type HawkFindPost,
} from "../services/ProductsService";
import { getHawkFindRetailerMeta, HAWK_FIND_RETAILER_OPTIONS } from "../config/hawkFindRetailers";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);

export default function HawkFindsPage() {
  const PAGE_SIZE = 12;
  const { user } = useUserContext();
  const navigate = useNavigate();
  const [pendingDeletePost, setPendingDeletePost] = useState<HawkFindPost | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRetailerFilter, setSelectedRetailerFilter] = useState<string>("all");
  const [deletingPostIds, setDeletingPostIds] = useState<Record<string, boolean>>({});
  const [reactionOverrides, setReactionOverrides] = useState<Record<string, {
    totalReactions: number;
    hasUserReaction: boolean;
    userReaction?: string;
    reactions?: Record<string, number>;
  }>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: getHawkFindsQueryKey(PAGE_SIZE),
    queryFn: ({ pageParam }) =>
      getHawkFinds(PAGE_SIZE, typeof pageParam === "string" ? pageParam : undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.nextCursor ? lastPage.nextCursor : undefined),
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const posts = useMemo(() => data?.pages.flatMap((p) => p.posts) ?? [], [data?.pages]);
  const displayPosts = useMemo(() => {
    return posts.map((post) => {
      if (!post.postId) return post;
      const override = reactionOverrides[post.postId];
      if (!override) return post;
      return { ...post, ...override };
    });
  }, [posts, reactionOverrides]);
  const availableRetailerFilters = useMemo(() => {
    const available = new Set(displayPosts.map((post) => post.retailer));
    return HAWK_FIND_RETAILER_OPTIONS.filter((option) => available.has(option.value));
  }, [displayPosts]);
  const filteredDisplayPosts = useMemo(() => {
    if (selectedRetailerFilter === "all") return displayPosts;
    return displayPosts.filter((post) => post.retailer === selectedRetailerFilter);
  }, [displayPosts, selectedRetailerFilter]);

  useEffect(() => {
    if (selectedRetailerFilter === "all") return;
    const isStillAvailable = availableRetailerFilters.some((option) => option.value === selectedRetailerFilter);
    if (!isStillAvailable) setSelectedRetailerFilter("all");
  }, [availableRetailerFilters, selectedRetailerFilter]);

  const virtualizer = useVirtualizer({
    count: filteredDisplayPosts.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 340,
    overscan: 3,
    gap: 16,
    paddingStart: 8,
    paddingEnd: 8,
  });

  const handleFeedScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasNextPage || isFetchingNextPage) return;
    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceToBottom < 320) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleCheckNearby = useCallback(async (post: HawkFindPost) => {
    const code = post.sku?.trim();
    if (!code) return;
    const retailer = getHawkFindRetailerMeta(post.retailer).searchRetailer;
    navigate(`/${retailer}/product-search?sku=${encodeURIComponent(code)}&auto=true`);
  }, [navigate]);

  const handleReact = useCallback(async (post: HawkFindPost, nextReaction: string) => {
    if (!post.postId) return;
    const previous = {
      totalReactions: post.totalReactions ?? 0,
      hasUserReaction: !!post.hasUserReaction,
      userReaction: post.userReaction,
      reactions: { ...(post.reactions ?? {}) } as Record<string, number>,
    };
    const toggledReaction = post.userReaction === nextReaction ? "" : nextReaction;
    const nextReactions: Record<string, number> = { ...previous.reactions };
    if (previous.userReaction) {
      const prevCount = nextReactions[previous.userReaction] ?? 0;
      if (prevCount <= 1) delete nextReactions[previous.userReaction];
      else nextReactions[previous.userReaction] = prevCount - 1;
    }
    if (toggledReaction) nextReactions[toggledReaction] = (nextReactions[toggledReaction] ?? 0) + 1;
    const optimisticTotal =
      previous.hasUserReaction && !toggledReaction
        ? Math.max(0, previous.totalReactions - 1)
        : !previous.hasUserReaction && !!toggledReaction
          ? previous.totalReactions + 1
          : previous.totalReactions;
    setReactionOverrides((prev) => ({
      ...prev,
      [post.postId!]: {
        ...previous,
        totalReactions: optimisticTotal,
        hasUserReaction: !!toggledReaction,
        userReaction: toggledReaction || undefined,
        reactions: nextReactions,
      },
    }));
    try {
      const summary = await setHawkFindReaction(post.postId, toggledReaction);
      setReactionOverrides((prev) => ({
        ...prev,
        [post.postId!]: {
          totalReactions: summary.totalReactions,
          hasUserReaction: summary.hasUserReaction,
          userReaction: summary.userReaction,
          reactions: summary.reactions,
        },
      }));
    } catch {
      setReactionOverrides((prev) => ({ ...prev, [post.postId!]: previous }));
    }
  }, []);

  const requestDeletePost = useCallback((post: HawkFindPost) => {
    if (!post.postId) return;
    setPendingDeletePost(post);
  }, []);

  const handleDeletePost = useCallback(async () => {
    if (!pendingDeletePost?.postId) return;
    const postId = pendingDeletePost.postId;
    const timestamp = pendingDeletePost.timestamp;
    setDeletingPostIds((prev) => ({ ...prev, [postId]: true }));
    try {
      await deleteHawkFind(postId, timestamp);
      setReactionOverrides((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
      await refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to delete Hawk Find";
      window.alert(message);
    } finally {
      setPendingDeletePost(null);
      setDeletingPostIds((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  }, [pendingDeletePost, refetch]);

  if (isLoading) return <LoadingState message="Loading Hawk Finds..." />;
  if (error) {
    return (
      <ErrorState
        title="Could not load Hawk Finds"
        message={error instanceof Error ? error.message : "Unknown error"}
        onRetry={refetch}
      />
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleFeedScroll}
      className="flex-1 min-h-0 overflow-y-auto bg-gray-100 dark:bg-dark-900 pb-16 md:pb-6"
    >
      <div className="max-w-lg mx-auto pt-4 pb-3">
        <div className="border border-gray-200 bg-white px-3 py-3 dark:border-dark-700 dark:bg-dark-800">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-300">
            About this feed
          </p>
          <h2 className="mt-1 text-base font-semibold text-gray-900 dark:text-white">Hawk Finds</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-dark-200">
            Deals discovered by the DealHawk team and shared by our community users.
          </p>
        </div>
      </div>
      <div className="max-w-lg mx-auto pb-2">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-2 px-1">
            <button
              onClick={() => setSelectedRetailerFilter("all")}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors whitespace-nowrap cursor-pointer ${
                selectedRetailerFilter === "all"
                  ? "bg-purple-600 border-purple-600 text-white"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-dark-800 dark:border-dark-600 dark:text-dark-100 dark:hover:bg-dark-700"
              }`}
              style={{ touchAction: "manipulation" }}
            >
              All Retailers
            </button>
            {availableRetailerFilters.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedRetailerFilter(option.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors whitespace-nowrap cursor-pointer ${
                  selectedRetailerFilter === option.value
                    ? "bg-purple-600 border-purple-600 text-white"
                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-dark-800 dark:border-dark-600 dark:text-dark-100 dark:hover:bg-dark-700"
                }`}
                style={{ touchAction: "manipulation" }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {posts.length === 0 ? (
        <div className="min-h-[220px] flex items-center justify-center px-4 py-10">
          <EmptyState title="No Hawk Finds yet" message="Check back soon for new posts from the team." />
        </div>
      ) : filteredDisplayPosts.length === 0 ? (
        <div className="min-h-[220px] flex items-center justify-center px-4 py-10">
          <EmptyState
            title="No deals for this retailer"
            message="Try another retailer filter or check back soon for new finds."
          />
        </div>
      ) : (
        <div className="max-w-lg mx-auto relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          {virtualizer.getVirtualItems().map((vi) => {
            const post = filteredDisplayPosts[vi.index];
            if (!post) return null;
            return (
              <div
                key={post.postId || `${post.userId}-${post.sku}-${post.timestamp}`}
                data-index={vi.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${vi.start}px)` }}
              >
                <HawkFindCard
                  post={post}
                  retailerLabel={getHawkFindRetailerMeta(post.retailer).label}
                  onCheckNearby={handleCheckNearby}
                  reactionOptions={HAWK_FIND_REACTIONS}
                  onReact={handleReact}
                  canDelete={user?.admin === true}
                  isDeleting={!!(post.postId && deletingPostIds[post.postId])}
                  onDelete={requestDeletePost}
                />
              </div>
            );
          })}
        </div>
      )}
      {isFetchingNextPage && (
        <div className="pb-8 text-center text-gray-400 dark:text-dark-400 text-sm">Loading more...</div>
      )}

      {user?.admin && (
        <button
          onClick={() => setShowCreateModal(true)}
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-12 h-12 rounded-full bg-purple-600 hover:bg-purple-500 active:bg-purple-700 text-white shadow-lg shadow-purple-600/40 flex items-center justify-center transition-colors cursor-pointer"
          style={{ touchAction: "manipulation" }}
        >
          <Plus size={22} />
        </button>
      )}

      <CreateHawkFindModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => refetch()}
      />
      <ConfirmActionModal
        isOpen={!!pendingDeletePost}
        title="Delete Hawk Find?"
        message="This post and all reactions will be permanently removed."
        confirmLabel="Delete"
        details={pendingDeletePost ? (
          <div className="rounded-lg border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900/60 p-2.5 flex items-center gap-2.5">
            <img
              src={pendingDeletePost.photo}
              alt={pendingDeletePost.title}
              className="w-14 h-14 rounded-md object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-900 dark:text-white line-clamp-2">
                {pendingDeletePost.title}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300 uppercase tracking-wide">
                {getHawkFindRetailerMeta(pendingDeletePost.retailer).label}
              </p>
              <p className="mt-1 text-xs text-gray-700 dark:text-dark-100">
                <span className="font-bold">{formatCurrency(pendingDeletePost.discountPrice)}</span>
                <span className="ml-1.5 text-gray-400 line-through">{formatCurrency(pendingDeletePost.ogPrice)}</span>
              </p>
            </div>
          </div>
        ) : null}
        isConfirming={!!(pendingDeletePost?.postId && deletingPostIds[pendingDeletePost.postId])}
        onConfirm={handleDeletePost}
        onClose={() => {
          if (pendingDeletePost?.postId && deletingPostIds[pendingDeletePost.postId]) return;
          setPendingDeletePost(null);
        }}
      />
    </div>
  );
}
